import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { GLIMState } from '@/brand';
import { Button } from '../components/ui/button';
import {
  Trophy, Globe, Eye, ChatCircle, ShoppingCart, ArrowUp, ArrowDown,
  Lightning, Robot, Warning, MagnifyingGlass, Graph, Microphone,
  PaintBrush, Gauge, CircleNotch, Lock, CheckCircle
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';
import { FabricatedDataNotice } from '../components/brand/FabricatedDataNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });

const MockBadge = () => (
  <span className="px-1.5 py-0.5 text-[9px] font-mono bg-[var(--warning-bg)] text-[var(--warning)] rounded ml-2">Mock</span>
);

const KpiStatus = ({ status }) => {
  const colors = { red: 'var(--error)', amber: 'var(--warning)', green: 'var(--success)' };
  return <span className="w-2 h-2 rounded-full inline-block" style={{ background: colors[status] || 'var(--text-tertiary)' }} />;
};

const AGENT_ICONS = { seo: MagnifyingGlass, auditor: MagnifyingGlass, strategist: Trophy, geo: Globe, entity: Graph, voice: Microphone, creative: PaintBrush, system: Robot };
const SEVERITY_COLORS = { high: 'var(--error)', medium: 'var(--warning)', low: 'var(--accent)' };

export default function DiscoveryRadarPage() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState('');
  const [vernToggle, setVernToggle] = useState('combined');

  useEffect(() => {
    axios.get(`${API}/discovery-radar`, auth())
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load radar'))
      .finally(() => setLoading(false));
  }, []);

  const triggerAgent = async (agentType) => {
    setTriggering(agentType);
    toast.success(`${agentType} agent triggered — executing workflow`);
    setTimeout(() => {
      setTriggering('');
      const routeMap = { geo: '/seo', entity: '/entity', creative: '/creative-lab', voice: '/voice' };
      if (routeMap[agentType]) navigate(routeMap[agentType]);
    }, 1500);
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><GLIMState state="discovering" size={72} label="Discovering" /></div></DashboardLayout>;

  const kpi = data?.kpi_cards || {};

  const radarData = [
    { metric: 'SoM', value: kpi.som?.value || 0 },
    { metric: 'Entity', value: data?.module_scores?.entity_authority || 0 },
    { metric: 'Voice', value: data?.module_scores?.voice_readiness || 0 },
    { metric: 'AIGVR', value: kpi.aigvr?.value || 0 },
    { metric: 'Vernacular', value: kpi.vernacular_index?.value || 0 },
  ];

  const somChartData = [
    { name: 'Your Brand', score: kpi.som?.value || 0, fill: 'var(--accent)' },
    ...(kpi.som?.competitors || []).map(c => ({ name: c.name, score: c.som, fill: 'var(--neutral-text)' })),
  ];

  const vernValue = vernToggle === 'india' ? kpi.vernacular_index?.india : vernToggle === 'gcc' ? kpi.vernacular_index?.gcc : kpi.vernacular_index?.value;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="discovery-radar-page">
        <FabricatedDataNotice reason="Some KPIs on this legacy page are generated, not measured. For a fully evidence-backed view, use AI Discovery in the primary navigation." />
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>
            Global Discovery Radar
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Mission Control — unified search intelligence across all discovery surfaces</p>
        </div>

        {/* ═══ TOP 5 KPI CARDS ═══ */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-cards">
          {/* Card 1: SoM */}
          <div className="nx-metric-card rounded-lg relative" data-testid="kpi-som">
            <div className="flex items-center gap-1.5 mb-2">
              <KpiStatus status={kpi.som?.status} />
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Share of Model</span>
              {kpi.som?.is_mock && <MockBadge />}
            </div>
            <p className="text-3xl font-black font-mono" style={{ color: kpi.som?.status === 'green' ? 'var(--success)' : kpi.som?.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>
              {kpi.som?.value || 0}%
            </p>
            <div className={`flex items-center gap-1 mt-1 text-xs font-mono ${(kpi.som?.delta_7d || 0) >= 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
              {(kpi.som?.delta_7d || 0) >= 0 ? <ArrowUp size={10} weight="bold" /> : <ArrowDown size={10} weight="bold" />}
              {kpi.som?.delta_7d > 0 ? '+' : ''}{kpi.som?.delta_7d || 0}% 7d
            </div>
          </div>

          {/* Card 2: Discovery Reach */}
          <div className="nx-metric-card rounded-lg" data-testid="kpi-reach">
            <div className="flex items-center gap-1.5 mb-2">
              <Globe size={12} className="text-[var(--success)]" weight="fill" />
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Discovery Reach</span>
              {kpi.discovery_reach?.is_mock && <MockBadge />}
            </div>
            <p className="text-3xl font-black text-[var(--success)] font-mono">{(kpi.discovery_reach?.value || 0).toLocaleString('en-IN')}</p>
            <div className="flex items-center gap-1 mt-1 text-xs font-mono text-[var(--success)]">
              <ArrowUp size={10} weight="bold" /> +{kpi.discovery_reach?.delta_30d_pct || 0}% 30d
            </div>
          </div>

          {/* Card 3: AIGVR */}
          <div className="nx-metric-card rounded-lg" data-testid="kpi-aigvr">
            <div className="flex items-center gap-1.5 mb-2">
              <KpiStatus status={kpi.aigvr?.status} />
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">AI Visibility</span>
              {kpi.aigvr?.is_mock && <MockBadge />}
            </div>
            <p className="text-3xl font-black font-mono" style={{ color: kpi.aigvr?.status === 'green' ? 'var(--success)' : kpi.aigvr?.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>
              {kpi.aigvr?.value || 0}%
            </p>
            <p className="text-[9px] text-[var(--text-tertiary)] font-mono mt-1">{kpi.aigvr?.must_win_prompts_tested || 0} must-win prompts tested</p>
          </div>

          {/* Card 4: Vernacular Index */}
          <div className="nx-metric-card rounded-lg" data-testid="kpi-vernacular">
            <div className="flex items-center gap-1.5 mb-2">
              <ChatCircle size={12} className="text-[var(--accent)]" weight="fill" />
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Vernacular Index</span>
              {kpi.vernacular_index?.is_mock && <MockBadge />}
            </div>
            <p className="text-3xl font-black text-[var(--accent)] font-mono">{vernValue || 0}%</p>
            <div className="flex items-center gap-1 mt-1">
              {['combined', 'india', 'gcc'].map(t => (
                <button key={t} onClick={() => setVernToggle(t)}
                  className={`px-1.5 py-0.5 text-[8px] font-mono uppercase rounded ${vernToggle === t ? 'bg-[var(--accent)] text-white' : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]'}`}>{t}</button>
              ))}
            </div>
            <p className="text-[8px] text-[var(--success)] font-mono mt-1">{kpi.vernacular_index?.growth_vs_english || 0}% faster than EN baseline</p>
          </div>

          {/* Card 5: Non-Google Conversions */}
          <div className="nx-metric-card rounded-lg" data-testid="kpi-non-google">
            <div className="flex items-center gap-1.5 mb-2">
              <ShoppingCart size={12} className="text-[var(--success)]" weight="fill" />
              <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">Non-Google Conv.</span>
              {kpi.non_google_conversions?.is_mock && <MockBadge />}
            </div>
            <p className="text-3xl font-black text-[var(--success)] font-mono">{kpi.non_google_conversions?.value || 0}</p>
            <p className="text-[9px] text-[var(--text-tertiary)] font-mono mt-1">${(kpi.non_google_conversions?.revenue || 0).toLocaleString()} revenue</p>
          </div>
        </div>

        {/* ═══ PROACTIVE ACTION INTELLIGENCE TABLE ═══ */}
        {data?.intelligence_rows?.length > 0 && (
          <div className="nx-metric-card rounded-lg" data-testid="intelligence-table">
            <h3 className="text-sm font-bold text-[var(--text-primary)] mb-4" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>
              <Lightning size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" />
              Proactive Action Intelligence
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[var(--border)]">
                    <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-wider p-3 w-[25%]">Indicator</th>
                    <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-wider p-3 w-[40%]">Intelligence</th>
                    <th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-wider p-3 w-[35%]">Proactive Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.intelligence_rows.map(row => (
                    <tr key={row.id} className="border-b border-[var(--border-light)] hover:bg-[var(--surface-sunken)]">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: SEVERITY_COLORS[row.severity] }} />
                          <div>
                            <p className="text-xs text-[var(--text-primary)] font-medium">{row.indicator}</p>
                            <p className="text-[10px] text-[var(--text-tertiary)] font-mono">{row.signal}</p>
                          </div>
                          {row.is_mock && <MockBadge />}
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{row.intelligence}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-[10px] text-[var(--text-secondary)] mb-2">{row.action}</p>
                        <Button size="sm" onClick={() => triggerAgent(row.agent)}
                          disabled={triggering === row.agent}
                          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[10px] h-7 px-3">
                          {triggering === row.agent ? <CircleNotch size={10} className="animate-spin mr-1" /> : <Lightning size={10} className="mr-1" />}
                          Trigger Agent
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ MIDDLE ROW: SoM Chart + Radar ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="nx-metric-card rounded-lg" data-testid="som-chart">
            <div className="flex items-center gap-2 mb-4">
              <Trophy size={18} className="text-[var(--accent)]" weight="fill" />
              <h3 className="text-sm font-bold text-[var(--text-primary)]" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>Share of Model — Brand vs Competitors</h3>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={somChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} />
                  <YAxis dataKey="name" type="category" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} width={100} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }} />
                  <Bar dataKey="score" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[9px] text-[var(--text-tertiary)] font-mono mt-1">Citation frequency in ChatGPT, Gemini, and Perplexity responses</p>
          </div>

          <div className="nx-metric-card rounded-lg" data-testid="radar-chart">
            <h3 className="text-sm font-bold text-[var(--text-primary)] mb-4" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>Discovery Readiness Score</h3>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="var(--border)" />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                  <PolarRadiusAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} domain={[0, 100]} />
                  <Radar dataKey="value" stroke="var(--accent)" fill="var(--accent)" fillOpacity={0.2} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ═══ PHASE 2 CARDS — LIVE DATA ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="phase2-cards">
          {/* Card 6: Brand Sentiment */}
          {data?.phase2_cards?.brand_sentiment && (() => {
            const bs = data.phase2_cards.brand_sentiment;
            return (
              <div className="nx-metric-card rounded-lg" data-testid="card-brand-sentiment">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <KpiStatus status={bs.status} />
                    <span className="text-xs font-bold text-[var(--text-primary)]">Brand Sentiment (AI Decoded)</span>
                    {!bs.is_live && <MockBadge />}
                  </div>
                  {bs.auto_trigger_correction && (
                    <span className="px-2 py-0.5 text-[9px] font-mono bg-[var(--error-bg)] text-[var(--error)] rounded">Auto-Correction Triggered</span>
                  )}
                </div>
                <div className="flex items-end gap-4 mb-3">
                  <p className="text-4xl font-black font-mono" style={{ color: bs.status === 'green' ? 'var(--success)' : bs.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>
                    {bs.score}
                  </p>
                  <span className="text-xs text-[var(--text-tertiary)] font-mono mb-1">/100</span>
                </div>
                <div className="flex items-center gap-1 h-3 rounded-full overflow-hidden mb-2">
                  <div className="h-full rounded-l-full bg-[var(--success)]" style={{ width: `${bs.positive_pct}%` }} title={`Positive: ${bs.positive_pct}%`} />
                  <div className="h-full bg-[var(--warning)]" style={{ width: `${bs.neutral_pct}%` }} title={`Neutral: ${bs.neutral_pct}%`} />
                  <div className="h-full rounded-r-full bg-[var(--error)]" style={{ width: `${bs.negative_pct}%` }} title={`Negative: ${bs.negative_pct}%`} />
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono">
                  <span className="text-[var(--success)]">{bs.positive_pct}% Positive</span>
                  <span className="text-[var(--warning)]">{bs.neutral_pct}% Neutral</span>
                  <span className="text-[var(--error)]">{bs.negative_pct}% Negative</span>
                </div>
                {bs.attributes_detected && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {bs.attributes_detected.positive?.map(a => <span key={a} className="px-1.5 py-0.5 text-[9px] bg-[var(--success-bg)] text-[var(--success)] rounded font-mono">{a}</span>)}
                    {bs.attributes_detected.negative?.map(a => <span key={a} className="px-1.5 py-0.5 text-[9px] bg-[var(--error-bg)] text-[var(--error)] rounded font-mono">{a}</span>)}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Card 7: Crawl-to-Referral */}
          {data?.phase2_cards?.crawl_referral && (() => {
            const cr = data.phase2_cards.crawl_referral;
            return (
              <div className="nx-metric-card rounded-lg" data-testid="card-crawl-referral">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-[var(--text-primary)]">Crawl-to-Referral Ratio</span>
                  {!cr.is_live && <MockBadge />}
                </div>
                <div className="flex items-end gap-3 mb-3">
                  <p className="text-3xl font-black text-[var(--accent)] font-mono">{cr.overall_ratio}%</p>
                  <span className="text-[10px] text-[var(--text-tertiary)] font-mono mb-1">{cr.total_referrals} referrals / {cr.total_crawls} crawls</span>
                </div>
                <div className="space-y-1.5">
                  {Object.entries(cr.bots || {}).map(([bot, d]) => (
                    <div key={bot} className={`flex items-center gap-2 p-1.5 rounded text-[10px] font-mono ${d.machine_tax_alert ? 'bg-[var(--error-bg)] border border-[var(--error-border)]' : 'bg-[var(--surface-sunken)]'}`}>
                      <span className={`w-16 ${d.machine_tax_alert ? 'text-[var(--error)]' : 'text-[var(--text-secondary)]'}`}>{bot.replace('Bot', '')}</span>
                      <span className="text-[var(--text-primary)] w-12 text-right">{d.crawls}</span>
                      <span className="text-[var(--text-tertiary)]">/</span>
                      <span className={`w-8 ${d.referrals === 0 ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{d.referrals}</span>
                      <span className="text-[var(--text-tertiary)] flex-1">{d.ratio_pct}%</span>
                      {d.machine_tax_alert && <Warning size={12} className="text-[var(--error)]" weight="fill" />}
                    </div>
                  ))}
                </div>
                {cr.alerts?.length > 0 && (
                  <p className="text-[9px] text-[var(--error)] font-mono mt-2">Machine Tax Alert: {cr.alerts.join(', ')} consuming crawl budget with zero return</p>
                )}
                {cr.source_note && <p className="text-[8px] text-[var(--text-tertiary)] mt-1 italic">{cr.source_note}</p>}
              </div>
            );
          })()}

          {/* Card 8: Content Extraction Rate */}
          {data?.phase2_cards?.content_extraction && (() => {
            const ce = data.phase2_cards.content_extraction;
            return (
              <div className="nx-metric-card rounded-lg" data-testid="card-cer">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <KpiStatus status={ce.status} />
                    <span className="text-xs font-bold text-[var(--text-primary)]">Content Extraction Rate (CER)</span>
                    {!ce.is_live && <MockBadge />}
                  </div>
                  <span className="text-[9px] text-[var(--text-tertiary)] font-mono">Benchmark: {ce.benchmark}%</span>
                </div>
                <div className="flex items-end gap-3 mb-3">
                  <p className="text-3xl font-black font-mono" style={{ color: ce.status === 'green' ? 'var(--success)' : ce.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>
                    {ce.overall_pct}%
                  </p>
                  <span className={`flex items-center gap-0.5 text-xs font-mono mb-1 ${ce.delta_30d >= 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                    {ce.delta_30d >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                    {ce.delta_30d > 0 ? '+' : ''}{ce.delta_30d}% 30d
                  </span>
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'Tables extracted', value: ce.breakdown?.tables_extracted },
                    { label: 'Lists extracted', value: ce.breakdown?.lists_extracted },
                    { label: 'FAQ blocks extracted', value: ce.breakdown?.faq_blocks_extracted },
                  ].map(b => (
                    <div key={b.label} className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--text-tertiary)] w-28">{b.label}</span>
                      <div className="flex-1 h-2 bg-[var(--surface-sunken)] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${b.value}%`, background: b.value >= 40 ? 'var(--success)' : b.value >= 25 ? 'var(--warning)' : 'var(--error)' }} />
                      </div>
                      <span className="text-[10px] font-mono text-[var(--text-primary)] w-10 text-right">{b.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Card 9: Buy-Box Visibility */}
          {data?.phase2_cards?.buybox_visibility && (() => {
            const bb = data.phase2_cards.buybox_visibility;
            if (!bb.applicable) return (
              <div className="nx-metric-card rounded-lg opacity-40" data-testid="card-buybox">
                <Lock size={18} className="text-[var(--text-tertiary)] mb-2" />
                <p className="text-xs text-[var(--text-tertiary)]">Buy-Box Visibility</p>
                <p className="text-[9px] text-[var(--neutral-text)] mt-1">{bb.reason}</p>
              </div>
            );
            return (
              <div className="nx-metric-card rounded-lg" data-testid="card-buybox">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <KpiStatus status={bb.status} />
                    <span className="text-xs font-bold text-[var(--text-primary)]">Buy-Box Visibility</span>
                    {!bb.is_live && <MockBadge />}
                  </div>
                  {bb.auto_trigger_marketplace && (
                    <span className="px-2 py-0.5 text-[9px] font-mono bg-[var(--error-bg)] text-[var(--error)] rounded">Marketplace Agent Triggered</span>
                  )}
                </div>
                <p className="text-3xl font-black font-mono mb-3" style={{ color: bb.status === 'green' ? 'var(--success)' : bb.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>
                  {bb.avg_win_rate}%
                </p>
                <div className="space-y-1.5">
                  {Object.entries(bb.platforms || {}).map(([mp, d]) => (
                    <div key={mp} className="flex items-center gap-2">
                      <span className="text-[10px] text-[var(--text-secondary)] w-20 capitalize">{mp.replace('_', ' ')}</span>
                      <div className="flex-1 h-2 bg-[var(--surface-sunken)] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${d.win_rate}%`, background: d.status === 'green' ? 'var(--success)' : d.status === 'amber' ? 'var(--warning)' : 'var(--error)' }} />
                      </div>
                      <span className={`text-[10px] font-mono w-10 text-right ${d.status === 'green' ? 'text-[var(--success)]' : d.status === 'amber' ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>{d.win_rate}%</span>
                      {d.auto_trigger && <Warning size={10} className="text-[var(--error)]" weight="fill" />}
                    </div>
                  ))}
                </div>
                {bb.alerts?.length > 0 && (
                  <p className="text-[9px] text-[var(--error)] font-mono mt-2">Alerts: {bb.alerts.join(', ')} below 70% threshold</p>
                )}
              </div>
            );
          })()}
        </div>

        {/* ═══ LIVE AGENT ACTIVITY FEED ═══ */}
        <div className="nx-metric-card rounded-lg" data-testid="execution-feed">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[var(--text-primary)]" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>
              <CheckCircle size={16} className="inline mr-2 text-[var(--success)]" weight="fill" />
              Live Agent Activity — Execution Proof
            </h3>
            <span className="w-2 h-2 rounded-full bg-[var(--success)] animate-pulse" />
          </div>
          <div className="space-y-2">
            {data?.execution_feed?.map((entry, i) => {
              const AgentIcon = AGENT_ICONS[entry.agent] || Robot;
              return (
                <div key={entry.id || i} className="flex items-start gap-3 p-3 rounded border border-[var(--border-light)] animate-fade-in-up"
                  style={{ animationDelay: `${i * 60}ms` }}>
                  <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                    entry.agent === 'seo' || entry.agent === 'auditor' ? 'bg-[var(--accent-light)]' :
                    entry.agent === 'strategist' ? 'bg-[var(--success-bg)]' :
                    entry.agent === 'geo' ? 'bg-[var(--warning-bg)]' : 'bg-[var(--surface-sunken)]'
                  }`}>
                    <AgentIcon size={16} className={
                      entry.agent === 'seo' || entry.agent === 'auditor' ? 'text-[var(--accent)]' :
                      entry.agent === 'strategist' ? 'text-[var(--success)]' :
                      entry.agent === 'geo' ? 'text-[var(--warning)]' : 'text-[var(--text-tertiary)]'
                    } weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[var(--text-primary)] leading-relaxed">{entry.action}</p>
                    {entry.result && <p className="text-[10px] text-[var(--text-secondary)] mt-0.5 line-clamp-1">{entry.result}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[9px] text-[var(--text-tertiary)] font-mono">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}</span>
                      {entry.client && <span className="text-[9px] text-[var(--accent)] font-mono">{entry.client}</span>}
                      {entry.is_mock && <MockBadge />}
                    </div>
                  </div>
                  <CheckCircle size={14} className="text-[var(--success)] flex-shrink-0 mt-1" weight="fill" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
